/**
 * /data - where every number in this model came from.
 *
 * The Ujjani record arrived as a pile of PDFs, spreadsheets and scanned
 * drawings that do not agree with each other, and three of those disagreements
 * had already reached the model as wrong numbers before anyone noticed. This
 * page is the audit: one row per parameter, the document behind it, and - where
 * two sources disagreed - the value that was rejected and why.
 *
 * The rejected column is the point of the page. Anyone can show a table of
 * values; showing what was discarded, and on what grounds, is what makes the
 * rest of it checkable.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, FileText } from 'lucide-react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { SpillwayRating } from '../components/SpillwayRating'
import { api } from '../api/client'
import type { Provenance, ProvenanceRow } from '../api/types'
import { C, fs } from '../design/tokens'

const STATUS_STYLE: Record<string, { chip: string; label: string; blurb: string }> = {
  confirmed: {
    chip: 'bg-emerald-950 text-emerald-300 border-emerald-800',
    label: 'Confirmed',
    blurb: 'Read from an official document and reproduced by a cross-check.',
  },
  derived: {
    chip: 'bg-sky-950 text-sky-300 border-sky-800',
    label: 'Derived',
    blurb: 'Computed from confirmed values; not itself published.',
  },
  assumed: {
    chip: 'bg-slate-800 text-slate-300 border-slate-700',
    label: 'Assumed',
    blurb: 'Our choice. Nobody has confirmed it.',
  },
  conflicting: {
    chip: 'bg-amber-950 text-amber-300 border-amber-800',
    label: 'Conflicting',
    blurb: 'Two sources disagree and it is not yet settled.',
  },
}

function formatValue(value: ProvenanceRow['value']): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'number') {
    if (Math.abs(value) >= 1e6) return value.toLocaleString(undefined, { maximumFractionDigits: 0 })
    if (Number.isInteger(value)) return String(value)
    return value.toFixed(Math.abs(value) < 10 ? 3 : 2)
  }
  return String(value)
}

export default function ProvenancePage() {
  const [filter, setFilter] = useState<string | null>(null)
  const query = useQuery({ queryKey: ['provenance'], queryFn: api.provenance })

  const grouped = useMemo(() => {
    if (!query.data) return []
    const shown = filter
      ? query.data.rows.filter((row) => row.status === filter)
      : query.data.rows
    const groups = new Map<string, ProvenanceRow[]>()
    for (const row of shown) {
      const list = groups.get(row.group) ?? []
      list.push(row)
      groups.set(row.group, list)
    }
    return [...groups.entries()]
  }, [query.data, filter])

  if (query.isPending) {
    return <p className="p-8 text-sm text-slate-500">Assembling the record&hellip;</p>
  }
  if (query.isError) {
    return (
      <p className="p-8 text-sm text-rose-400">
        {query.error instanceof Error ? query.error.message : 'Could not load provenance'}
      </p>
    )
  }

  const data: Provenance = query.data
  // The drawdown is a path down this very curve, so it shares the level axis:
  // at each level the breach passed through, how much storage was left.
  const drawdownByLevel = new Map<number, number>()
  const dd = data.drawdown
  if (dd?.level_m && dd.storage_m3) {
    for (let i = 0; i < dd.level_m.length; i += 1) {
      drawdownByLevel.set(Math.round(dd.level_m[i] * 20) / 20, dd.storage_m3[i] / 1e6)
    }
  }
  const chart = data.curve.level_m.map((level, index) => {
    const key = Math.round(level * 20) / 20
    const traversed =
      dd?.initial_level_m != null &&
      dd?.final_level_m != null &&
      level <= dd.initial_level_m &&
      level >= dd.final_level_m
    return {
      level,
      storage: data.curve.storage_m3[index] / 1e6,
      area: data.curve.area_m2[index] / 1e6,
      drawdown: traversed
        ? (drawdownByLevel.get(key) ?? data.curve.storage_m3[index] / 1e6)
        : null,
    }
  })

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-6xl p-6">
        <header>
          <h2 className="text-base font-semibold text-slate-100">Data provenance</h2>
          <p className="mt-1 max-w-3xl text-sm leading-relaxed text-slate-400">
            Every parameter this model uses, the document it came from, and &mdash;
            where two official sources disagreed &mdash; the value that was rejected
            and why. Three of the disagreements below had already reached the model as
            wrong numbers before the source documents arrived.
          </p>
        </header>

        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(STATUS_STYLE).map(([key, style]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(filter === key ? null : key)}
              title={style.blurb}
              className={[
                'rounded border px-2.5 py-1 text-xs transition-opacity',
                style.chip,
                filter && filter !== key ? 'opacity-40' : '',
              ].join(' ')}
            >
              {style.label}{' '}
              <span className="tabular-nums opacity-70">{data.counts[key] ?? 0}</span>
            </button>
          ))}
          {filter && (
            <button
              type="button"
              onClick={() => setFilter(null)}
              className="rounded border border-slate-700 px-2.5 py-1 text-xs text-slate-400 hover:text-slate-200"
            >
              Show all
            </button>
          )}
        </div>

        {/* --- the curve ---------------------------------------------------- */}
        <section className="mt-6 rounded-md border border-slate-800 bg-slate-900 p-4">
          <h3 className="text-xs font-medium uppercase tracking-wider text-slate-400">
            Elevation &ndash; area &ndash; capacity
          </h3>
          <p className="text-12 text-slate-500">
            The official area-capacity table, {data.curve.source === 'table' ? 'in use' : 'NOT in use'}.
            Below 484.00 m the table stops and the curve is extended to the river bed &mdash;
            that region holds 23% of gross storage and a full breach drains through it.
            {data.drawdown?.initial_level_m != null && (
              <>
                {' '}The <span className="text-rose-400">red band</span> is the reservoir
                emptying during the base-case breach, routed on this same curve:{' '}
                {data.drawdown.initial_level_m.toFixed(2)} m down to{' '}
                {data.drawdown.final_level_m?.toFixed(2)} m in{' '}
                {data.drawdown.time_h?.[data.drawdown.time_h.length - 1]?.toFixed(0)} h.
              </>
            )}
          </p>
          <div className="mt-3 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 8, right: 56, bottom: 18, left: 4 }}>
                <CartesianGrid stroke={C.grid} />
                <XAxis
                  dataKey="level"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tick={{ fontSize: fs(12), fill: C.textFaint }}
                  stroke={C.grid}
                  tickFormatter={(value: number) => value.toFixed(0)}
                  label={{
                    value: 'Reservoir level (m)',
                    position: 'insideBottom',
                    offset: -12,
                    fill: C.textFaint,
                    fontSize: fs(12),
                  }}
                />
                <YAxis
                  yAxisId="storage"
                  tick={{ fontSize: fs(12), fill: C.textFaint }}
                  stroke={C.grid}
                  width={56}
                  tickFormatter={(value: number) => `${Math.round(value)}`}
                />
                <YAxis
                  yAxisId="area"
                  orientation="right"
                  tick={{ fontSize: fs(12), fill: C.textFaint }}
                  stroke={C.grid}
                  width={48}
                />
                <Tooltip
                  contentStyle={{
                    background: C.bg,
                    border: '1px solid var(--ink-grid)',
                    fontSize: fs(12),
                  }}
                  labelFormatter={(value: number) => `${value.toFixed(2)} m`}
                  formatter={(value: number, name: string) => [
                    name === 'Storage'
                      ? `${value.toFixed(1)} Mm³`
                      : `${value.toFixed(1)} km²`,
                    name,
                  ]}
                />
                {data.marks.map((mark) => (
                  <ReferenceLine
                    key={mark.label}
                    yAxisId="storage"
                    x={mark.level_m}
                    stroke={mark.label.startsWith('FRL') ? C.warn : C.textFaint}
                    strokeDasharray="3 3"
                    label={{
                      value: mark.label,
                      angle: -90,
                      position: 'insideTopLeft',
                      fill: mark.label.startsWith('FRL') ? C.warn : C.textFaint,
                      fontSize: fs(12),
                    }}
                  />
                ))}
                <Line
                  yAxisId="storage"
                  type="monotone"
                  dataKey="storage"
                  name="Storage"
                  stroke={C.accent}
                  strokeWidth={1.8}
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="storage"
                  type="monotone"
                  dataKey="drawdown"
                  name="Breach drawdown"
                  stroke={C.alert}
                  strokeWidth={3.5}
                  strokeOpacity={0.55}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="area"
                  type="monotone"
                  dataKey="area"
                  name="Water surface area"
                  stroke={C.accentDim}
                  strokeWidth={1.2}
                  strokeDasharray="4 2"
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-12 tabular-nums text-slate-500">
            {data.marks.map((mark) => (
              <span key={mark.label}>
                <span className="text-slate-400">{mark.label}</span>{' '}
                {mark.level_m.toFixed(2)} m &middot; {(mark.storage_m3 / 1e6).toFixed(0)} Mm³
                &middot; {(mark.area_m2 / 1e6).toFixed(0)} km²
              </span>
            ))}
          </div>
        </section>

        {/* --- the table ---------------------------------------------------- */}
        {grouped.map(([group, groupRows]) => (
          <section key={group} className="mt-6">
            <h3 className="text-xs font-medium uppercase tracking-wider text-slate-400">
              {group}
            </h3>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full min-w-[1000px] text-left text-xs">
                <thead className="text-12 uppercase tracking-wider text-slate-500">
                  <tr className="border-b border-slate-800">
                    <th className="py-1.5 pr-3 font-medium">Parameter</th>
                    <th className="py-1.5 pr-3 text-right font-medium">Value</th>
                    <th className="py-1.5 pr-3 font-medium">Unit</th>
                    <th className="py-1.5 pr-3 font-medium">Status</th>
                    <th className="py-1.5 pr-3 font-medium">Source</th>
                    <th className="py-1.5 font-medium">Rejected, and why</th>
                  </tr>
                </thead>
                <tbody>
                  {groupRows.map((row) => {
                    const style = STATUS_STYLE[row.status] ?? STATUS_STYLE.assumed
                    return (
                      <tr
                        key={row.parameter}
                        className="border-b border-slate-800/60 align-top"
                      >
                        <td className="py-2 pr-3 font-medium text-slate-200">
                          {row.parameter}
                          {row.note && (
                            <p className="mt-0.5 max-w-md text-12 font-normal leading-snug text-slate-500">
                              {row.note}
                            </p>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-slate-100">
                          {formatValue(row.value)}
                        </td>
                        <td className="py-2 pr-3 text-slate-500">{row.unit}</td>
                        <td className="py-2 pr-3">
                          <span
                            className={`inline-block whitespace-nowrap rounded border px-1.5 py-0.5 text-12 ${style.chip}`}
                          >
                            {style.label}
                          </span>
                        </td>
                        <td className="py-2 pr-3 max-w-[15rem] text-12 leading-snug text-slate-400">
                          <span className="inline-flex gap-1">
                            <FileText className="mt-0.5 h-3 w-3 shrink-0 text-slate-600" />
                            {row.source}
                          </span>
                        </td>
                        <td className="py-2 max-w-md text-12 leading-snug">
                          {row.rejected_value === null ? (
                            <span className="text-slate-600">&mdash;</span>
                          ) : (
                            <>
                              <span className="text-rose-400 line-through">
                                {formatValue(row.rejected_value)}
                              </span>{' '}
                              <span className="text-slate-500">
                                ({row.rejected_source})
                              </span>
                              <p className="mt-0.5 text-slate-400">{row.rejected_why}</p>
                            </>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ))}

        {data.spillway_rating && <SpillwayRating data={data.spillway_rating} />}

        <section className="my-8 rounded-md border border-amber-900/60 bg-amber-950/20 p-4">
          <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5" />
            Still outstanding
          </h3>
          <ul className="mt-2 space-y-1.5">
            {data.open_questions.map((question) => (
              <li key={question} className="text-12 leading-snug text-slate-300">
                &mdash; {question}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  )
}
